import type { DocumentDto } from "../../api/types";
import { Markdown } from "../media/Markdown";
import { toEmbedUrl } from "../../lib/format";

/**
 * Renders the type-specific body of a document: a full image for photographs,
 * a player for films, the Markdown body for journal entries, and a prominent
 * outbound link box for external references (links, documents, articles).
 */
export function DocumentBody({ doc }: { doc: DocumentDto }) {
  switch (doc.contentType) {
    case "PHOTO":
      return (
        <>
          <div className="doc__media">
            <img src={doc.url} alt={doc.title} />
          </div>
          {doc.summary && <p className="doc__body">{doc.summary}</p>}
        </>
      );

    case "VIDEO":
      return (
        <>
          <VideoPlayer doc={doc} />
          {doc.summary && <p className="doc__body">{doc.summary}</p>}
        </>
      );

    case "BLOG_POST":
      return (
        <>
          {doc.thumbnailUrl && (
            <div className="doc__media">
              <img src={doc.thumbnailUrl} alt={doc.title} />
            </div>
          )}
          {doc.bodyMarkdown ? (
            <Markdown>{doc.bodyMarkdown}</Markdown>
          ) : (
            <p className="muted">This entry has no body yet.</p>
          )}
        </>
      );

    // LINK / DOCUMENT / ARTICLE
    default:
      return (
        <>
          {doc.summary && <p className="doc__body">{doc.summary}</p>}
          <div className="doc__linkbox">
            <strong>External {doc.contentType.toLowerCase()}</strong>
            {doc.source ? ` · ${doc.source}` : ""}
            <br />
            <a href={doc.url} target="_blank" rel="noopener noreferrer">
              {doc.url} ↗
            </a>
          </div>
        </>
      );
  }
}

function VideoPlayer({ doc }: { doc: DocumentDto }) {
  if (doc.provider === "FILE") {
    return (
      <div className="doc__media">
        <video src={doc.url} controls poster={doc.thumbnailUrl ?? undefined}>
          Your browser does not support embedded video.
        </video>
      </div>
    );
  }
  const embed = toEmbedUrl(doc.url, doc.provider ?? "FILE");
  if (embed) {
    const tall = doc.provider === "TIKTOK";
    return (
      <div className={`doc__media${tall ? " doc__media--tall" : ""}`}>
        <iframe
          src={embed}
          title={doc.title}
          allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
          allowFullScreen
        />
      </div>
    );
  }
  // A plain link (LINK) or a platform URL we could not turn into a player.
  return (
    <div className="doc__linkbox">
      {doc.thumbnailUrl && (
        <a href={doc.url} target="_blank" rel="noopener noreferrer" className="doc__linkthumb">
          <img src={doc.thumbnailUrl} alt="" />
        </a>
      )}
      <strong>Watch on {hostOf(doc.url)}</strong>
      <br />
      <a href={doc.url} target="_blank" rel="noopener noreferrer">
        {doc.url} ↗
      </a>
    </div>
  );
}

/** "www.example.com" → "example.com", for "Watch on …"; the raw URL if unparseable. */
function hostOf(url: string): string {
  try {
    return new URL(url).hostname.replace(/^www\./, "");
  } catch {
    return "the original site";
  }
}
